import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Mic, NextIcon } from "./Icons";
import useIsMobile from "../hooks/useIsMobile";
import Markdown from "react-markdown";
import Loader from "./Loader";
import { useTranslation } from "react-i18next";
import { DefaultTranscriptMessage } from "ta-agent-sdk/react";

type MessageBubbleProps = {
  content: string;
};

const transition = {
  type: "spring" as const,
  stiffness: 420,
  damping: 42,
  mass: 0.7,
};

const UserMessage = ({ content }: MessageBubbleProps) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 1.12, y: 28 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95, y: -12 }}
    transition={transition}
    style={{ transformOrigin: "bottom right" }}
    className="rounded-xl rounded-br-none bg-white text-black p-2 px-3 text-sm self-end shadow-sm max-w-[90%]"
  >
    {content}
  </motion.div>
);

const AgentMessage = ({ content }: MessageBubbleProps) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 1.12, y: 28 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95, y: -12 }}
    transition={transition}
    style={{ transformOrigin: "bottom left" }}
    className="bg-blue-500 rounded-xl rounded-bl-none text-white p-2 px-3 text-sm self-start shadow-sm max-w-[90%]"
  >
    <Markdown>{content}</Markdown>
  </motion.div>
);

function ChatBox({
  messages: _messages,
  muted,
  setMuted,
  sendMessage,
  disabled,
  showChat,
  setShowChat,
  setShowControlTray,
}: {
  messages: Readonly<DefaultTranscriptMessage[]>;
  muted: boolean;
  setMuted: (muted: boolean) => void;
  sendMessage: (message: string) => void;
  disabled: boolean;
  showChat: boolean;
  setShowChat: (show: boolean) => void;
  setShowControlTray: (show: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [focused, setFocused] = useState(false);
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const maxRows = 4;
  const [messages, setMessages] = useState<DefaultTranscriptMessage[]>([]);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  useEffect(() => {
    setMessages(_messages.slice(-3));
  }, [_messages]);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const computedLineHeight = window.getComputedStyle(textarea).lineHeight;
    const lineHeight =
      computedLineHeight === "normal" ? 20 : parseFloat(computedLineHeight);
    const maxHeight = lineHeight * maxRows;
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 20), maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [message]);

  useEffect(() => {
    if (((showChat && isMobile) || focused) && muted === false) {
      setMuted(true);
    }
  }, [focused, muted, setMuted, showChat, isMobile]);

  useEffect(() => {
    if (showChat && isMobile) {
      setShowControlTray(false);
    } else {
      setShowControlTray(true);
    }
  }, [showChat, isMobile, setShowControlTray]);

  const handleSubmit = () => {
    if (!message.trim() || disabled) {
      return;
    }

    sendMessage(message.trim());

    setMessage("");
  };

  useEffect(() => {
    if (showChat) {
      textareaRef.current?.focus();
    }
  }, [showChat]);

  return (
    <div
      className={`${
        showChat ? "" : "hidden"
      } fixed bottom-0 md:bottom-10 left-0 right-0 md:right-auto mx-auto md:left-10 p-2 md:p-3 transition-all duration-200 z-[102] ${
        focused || messages.length > 0 ? "bg-black/20 backdrop-blur shadow" : ""
      } w-[100%] md:w-[300px] md:rounded-[30px] `}
    >
      <div
        ref={messageContainerRef}
        className={`flex flex-col h-[200px] md:max-h-[300px] overflow-auto md:rounded-[20px] ${
          messages.length > 0 ? "mb-3" : ""
        } gap-1 md:gap-2`}
      >
        <AnimatePresence initial={false}>
          {messages.map((msg) =>
            msg.role === "user" ? (
              <UserMessage key={msg.id} content={msg.text} />
            ) : (
              <AgentMessage key={msg.id} content={msg.text} />
            )
          )}
        </AnimatePresence>
      </div>
      <div className="flex items-center bg-white rounded-[25px] overflow-hidden md:p-1">
        <textarea
          ref={textareaRef}
          className="w-full border-none outline-none pl-2 py-1 md:py-2 text-base text-[var(--ui-dark)] resize-none"
          placeholder={t("chat.placeholder")}
          rows={1}
          value={message}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        ></textarea>
        <button
          className="md:hidden text-[var(--ui-dark)] px-2"
          onClick={() => {
            setMuted(false);
            setShowChat(false);
            setShowControlTray(true);
          }}
        >
          <Mic className="w-[20px] h-[20px]" />
        </button>
        <button
          className="bg-[var(--ui-blue)] text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSubmit}
          disabled={disabled}
        >
          {disabled ? (
            <Loader />
          ) : (
            <NextIcon className="w-[24px] h-[24px] -rotate-90" />
          )}
        </button>
      </div>
    </div>
  );
}

export default ChatBox;
